<template>
    <div class="mt-4" id="priceAlertsTable">
        <template v-if="!priceAlertAdd">
            <div class="border-b pb-2 xs:mb-1 print:hidden">
                <div class="md:flex md:justify-between items-center">
                    <div v-if="haveItems">
                        <div class="inline-flex">
                            <div class="flex flex-wrap">
                                <div class="text-xs sm:text-sm font-semibold mr-2">Go to:</div>
                                <template v-for="(section, index) in filteredSections" :key="index">
                                    <a @click.prevent="scrollTo(`scrollTo-${index}`)" class="align-middle text-xs sm:text-sm pr-2 font-semibold cursor-pointer">
                                        {{ section.label }}
                                    </a>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="inline-flex ml-auto mb-1">
                        <span @click="priceAlertDeactivateModal=true" v-if="haveItems" class="btn btn-sm btn-red-ghost font-bold cursor-pointer text-sm mr-2">Deactivate Alerts</span>
                        <span @click="showAlertSettings" class="btn btn-sm btn-blue-ghost font-bold cursor-pointer text-sm">Notification Preferences</span>
                    </div>
                </div>
            </div>

            <div v-if="haveItems">
                <template v-for="(section, index) in sections">
                    <div class="mt-2" v-if="haveSectionItems(section)">
                        <div class="border-t-2 border-blue-600 mt-4 mb-6">
                            <price-alerts-table
                                @onAlertState="onAlertState"
                                @onAlertAction="onAlertAction"
                                @onAlertSort="onAlertSort"
                                :id="`scrollTo-${index}`"
                                :init-price-alert-data="priceAlertData"
                                :section="section"
                                :buttons="buttonOptions"
                            ></price-alerts-table>
                        </div>
                    </div>
                </template>
            </div>
            <alert-box class="mb-3" v-else>
                <template #body>
                    No price alerts have been added. Please click one of the buttons below to add a price alert.
                </template>
            </alert-box>

            <div class="flex flex-wrap justify-center pt-3 mt-8 print:hidden">
                <a @click="onAlertAction(section.name, 'add')" class="btn btn-sm btn-blue inline-block mx-1 mb-1" v-for="section in sections">
                    Add {{ section.label }}
                </a>
            </div>

            <modal @close="priceAlertDeactivateModal = null" v-if="priceAlertDeactivateModal" className="max-w-sm w-full">
                <template #header>
                    <div class="text-red-500">
                        <i class="fas fa-exclamation-circle mr-1"></i>
                        Deactivate Alerts
                    </div>
                </template>
                <template #body>
                    <div class="pt-4 pb-2 mb-3">
                        <p>Deactivating alerts will make them all «Inactive» and incapable of triggering.</p>
                        <p class="mt-2 font-bold">Are you sure you want to Deactivate ALL Alerts?</p>
                    </div>
                </template>

                <template #footer>
                    <div class="flex justify-center">
                        <div class="btn btn-sm btn-red-ghost inline-flex mx-1" @click="priceAlertDeactivateModal = false">Cancel</div>
                        <div class="btn btn-sm btn-red inline-flex mx-1" @click="deactivateAlerts">Deactivate</div>
                    </div>
                </template>
            </modal>

            <modal id="priceAlertSettingsModal" @close="priceAlertSettingsModal=false" v-if="priceAlertSettingsModal">
                <template #header>Notification Preferences</template>
                <template #body>
                    <div class="max-w-xl">
                        <p class="text-sm leading-tight mb-4">
                            Select how you want to receive «Price Alerts» below.
                            To add or update text alerts please visit the <a class="text-sm font-bold" :href="getNotificationLink()">«Notification Preferences»</a> page.
                        </p>
                    </div>
                    <div class="w-full my-2" v-if="priceAlertSettingsLoaded">
                        <fieldset class="block" :disabled="!userNotifications.notifications.email && !userNotifications.email">
                            <label class="block mb-2" for="email">
                                <input type="checkbox" id="email" class="form-control form-control-sm mb-1 mr-2" :value="userNotifications.notifications.email" v-model="userNotifications.notifications.email">
                                <span>Email Alerts</span>
                            </label>
                        </fieldset>
                        <fieldset class="block" :disabled="!userNotifications.notifications.sms && !userNotifications.phone">
                            <label class="block" for="sms">
                                <input type="checkbox" id="sms" class="form-control form-control-sm mb-1 mr-2" :value="userNotifications.notifications.sms" v-model="userNotifications.notifications.sms">
                                <span>Text Message Alerts</span>
                            </label>
                        </fieldset>
                    </div>
                    <spinner class="my-1" v-else></spinner>
                </template>
                <template #footer>
                    <div class="flex justify-center">
                        <div @click="priceAlertSettingsModal=false" class="mx-1 btn btn-yellow-ghost btn-sm">Cancel</div>
                        <div v-if="priceAlertSettingsLoaded" @click="updateNotifications" class="mx-1 btn btn-sm btn-yellow">Save</div>
                    </div>
                </template>
            </modal>

        </template>

        <price-alerts-modal
            v-if="priceAlertModal"
            @close="closePriceAlertModal"
            @onModalAction="onModalAction"
            :price-alert="priceAlertModal"
            :price-alerts="priceAlertData.alerts"
        >
        </price-alerts-modal>

        <flash-message :secondsToDisplay="3" ref="flash"></flash-message>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, getCurrentInstance } from 'vue';
import PriceAlertsTable from './PriceAlerts/PriceAlertsTable';
import PriceAlertsModal from './PriceAlerts/PriceAlertsModal';

import AlertManager from '@barchart/alerts-client-js/lib/AlertManager';
import JwtProvider from '@barchart/alerts-client-js/lib/security/JwtProvider';

const gbePriceAlertEndpoint = (process.env.NODE_ENV === 'production') ? window.route('api.price-alerts') : 'https://www.commoditytrends.com/api/price-alerts';

// Props
const props = defineProps({
    sectionTypes: Object,
    buttonOptions: Object,
    priceAlertAdd: Object,
    initSections: {
        type: Object,
        default: () => ({
            'futures': { name: 'futures', label: 'Futures', sortField: 'name', sortDir: 'asc' },
            'forex': { name: 'forex', label: 'Forex', sortField: 'name', sortDir: 'asc' },
            'future-spreads': { name: 'future-spreads', label: 'Futures Spread', sortField: 'name', sortDir: 'asc' },
            'future-options': { name: 'future-options', label: 'Options', sortField: 'name', sortDir: 'asc' },
            'stock-etf': { name: 'stock-etf', label: 'Stocks & ETFs', sortField: 'name', sortDir: 'asc' },
            'crypto': { name: 'crypto', label: 'Crypto', sortField: 'name', sortDir: 'asc' },
        }),
    },
});

const emit = defineEmits(['close']);
const { appContext } = getCurrentInstance();

// Reactive states
const alertManager = ref({});
const alertsCredentials = ref({
    'user_id': `${window.currentUser.id}` || '22',
    'alert_system': 'gbemembers.com'
});

const flash = ref(null);
const sections = ref(props.initSections);
const priceAlertData = ref({
    alerts: null,
    quotes: null,
});
const symbols = ref(null);
const loadingQuotes = ref(false);
const userNotifications =ref({
    notifications: {
        user_id: window.currentUser.id,
        name: 'price-alert',
        type: 'trade',
        email: false,
        sms: false
    },
    email: '',
    phone: ''
});
const priceAlertModal = ref(null);
const priceAlertSettingsModal = ref(false);
const priceAlertSettingsLoaded = ref(false);
const priceAlertDeactivateModal = ref(false);

// Computed properties
const filteredSections = computed(() => {
    return Object.entries(sections.value)
        .filter(([key, section]) => haveSectionItems(section))
        .map(([key, section]) => section);
});

const offset = computed(() => {
    const thead = document.querySelector('table.table thead');
    return thead.getBoundingClientRect().height * -1;
});

const scrollTo = (element) => {
    const options = {
        offset: offset.value,
        container: document.body,
    };
    appContext.app.config.globalProperties.$scrollTo(`#${element}`, undefined, options);
};

const haveItems = computed(() => {
    var items = false;
    if ((priceAlertData.value.alerts && Object.keys(priceAlertData.value['alerts']).length > 0)) {
        Object.keys(sections.value).forEach(section => {
            if (haveSectionItems(sections.value[section])) {
                items = true;
            }
        });
    }
    return items;
});

// Methods
const deactivateAlerts = () => {
    Object.keys(priceAlertData.value.alerts).forEach(symbol => {
        priceAlertData.value.alerts[symbol].forEach(alert => {
            if (alert.state !== 'Inactive') {
                alertManager.value.disableAlert({ 'user_id': alert.user_id, 'alert_id': alert.alert_id })
                    .catch((e) => {
                        flash.value.error(e);
                    });
            }
        });
    });

    flash.value.display('Alerts has been deactivated!');
    priceAlertDeactivateModal.value = false;
};

const showAlertSettings = () => {
    const route = window.route('my-folder.price-alerts.notifications', { name: 'price-alert', type: 'trade' });
    axios.get(route)
        .then((response) => {
            userNotifications.value = response.data;
            if (!userNotifications.value.notifications) {
                userNotifications.value.notifications = {
                    user_id: window.currentUser.id,
                    name: 'price-alert',
                    type: 'trade',
                    email: false,
                    sms: false
                };
            }
            priceAlertSettingsLoaded.value = true;
        }).catch(error => {
            // Handle error
        });

    priceAlertSettingsModal.value = true;
};

const updateNotifications = () => {
    const route = window.route('main.profile.notifications.update');
    axios.post(route, userNotifications.value.notifications)
        .then((response) => {
            flash.value.display('Successfully saved «Notification Preferences»!');
            priceAlertSettingsModal.value = false;
        }).catch(error => {
            flash.value.error('Error saving «Notification Preferences»');
        });
};

const getNotificationLink = () => {
    return window.route('main.profile.notifications');
};

const haveSectionItems = (section) => {
    let items = false;
    Object.keys(priceAlertData.value.alerts).forEach(symbol => {
        if (priceAlertData.value.alerts[symbol][0].notes) {
            if (priceAlertData.value.alerts[symbol][0].notes.section && priceAlertData.value.alerts[symbol][0].notes.section === section.name) {
                items = true;
            }
        }
    });
    return items;
};

const closePriceAlertModal = () => {
    priceAlertModal.value = null;
    emit('close');
};

const onModalAction = (request) => {
    priceAlertModal.value = null;
    if (request.action === 'add') {
        createAlert(request.data);
    } else if (request.action === 'edit') {
        updateAlert(request.item, request.data);
    }
    if (props.priceAlertAdd) {
        setTimeout(() => {
            cleanup();
            emit('close');
        }, 2500);
    }
};

const onAlertSort = (section) => {
    if (section.name) {
        sections.value[section.name] = section;
    }
};

const onAlertState = (alertItem, state) => {
    if (state === 'Active') {
        alertManager.value.enableAlert({ 'user_id': alertItem.user_id, 'alert_id': alertItem.alert_id })
            .catch((e) => {
                flash.value.error(e);
            });
    } else {
        alertManager.value.disableAlert({ 'user_id': alertItem.user_id, 'alert_id': alertItem.alert_id })
            .catch((e) => {
                flash.value.error(e);
            });
    }
};

const onAlertAction = (alertItem, action) => {
    if (action === 'add') {
        priceAlertModal.value = {
            action: 'add',
            section: sections.value[alertItem]
        };
    } else if (action === 'edit') {
        priceAlertModal.value = {
            action: 'edit',
            section: sections.value[alertItem[0].notes.section],
            symbol: alertItem[0].notes.symbol,
            alertItem: alertItem,
        };
    } else if (action === 'delete') {
        alertItem.forEach(a => {
            alertManager.value.deleteAlert({ 'alert_id': a.alert_id })
                .then(() => {
                    flash.value.display('The alert «' + a.notes.contractName + '» has been removed!')
                })
                .catch((e) => {
                    flash.value.error('There was an error deleting your alert!');
                });
        });
        // removeAlertData.value = null;
    }
};

const createAlert = (alertData) => {
    if (!alertData.symbol) {
        flash.value.error('There was an error creating your alert!');
        return;
    }

    let symbol = alertData.symbol.toString();

    ['above', 'below'].forEach(priceType => {
        if (alertData[priceType].lastPrice) {
            let operator_id = (priceType === 'above') ? 2 : 3;
            let conditions = [{
                'property': { 'property_id': 1, 'target': { 'identifier': symbol } },
                'operator': { 'operator_id': operator_id, 'operand': alertData[priceType].lastPrice.toString(), 'operand_display': alertData[priceType].lastPrice.toString() }
            }];

            let eodSchedules = [
                {
                    'time': '17:30',
                    'day': 'Monday',
                    'timezone': 'America/Chicago'
                },
                {
                    'time': '17:30',
                    'day': 'Tuesday',
                    'timezone': 'America/Chicago'
                },
                {
                    'time': '17:30',
                    'day': 'Wednesday',
                    'timezone': 'America/Chicago'
                },
                {
                    'time': '17:30',
                    'day': 'Thursday',
                    'timezone': 'America/Chicago'
                },
                {
                    'time': '17:30',
                    'day': 'Friday',
                    'timezone': 'America/Chicago'
                }
            ];

            let publishers = [{
                'type': { 'publisher_type_id': 3 },
                'use_default_recipient': false,
                'recipient': gbePriceAlertEndpoint,
                'format': null
            }];

            let system_notes = {
                'priceType': priceType,
                'checkType': alertData[priceType].checkType,
                'lastPrice': alertData[priceType].lastPrice,
                'lastPriceFormatted': alertData[priceType].lastPriceFormatted,
                'symbol': alertData.symbol,
                'section': alertData.section,
                'contractName': alertData.contractName,
            };

            let alert = {
                'name': alertData.contractName || alertData.symbol,
                'user_id': alertsCredentials.value.user_id, // Assuming alertsCredentials is a ref
                'alert_system': alertsCredentials.value.alert_system,
                'automatic_reset': false,
                'alert_type': 'price',
                'alert_behavior': (alertData[priceType].checkType == 'eod') ? 'schedule' : 'terminate',
                'user_notes': alertData.contractName || alertData.symbol,
                'notes': system_notes,
                'conditions': conditions,
                'publishers': publishers,
                'schedules': (alertData[priceType].checkType == 'eod') ? eodSchedules : null,
            };

            // Create new alert
            alertManager.value.createAlert(alert)
                .then((created) => {
                    flash.value.display('Alert for «' + alertData.contractName + '» has been saved!');
                    // Activate the alert
                    if (alertData[priceType].action === 'Active') {
                        alertManager.value.enableAlert({ 'user_id': created.user_id, 'alert_id': created.alert_id })
                            .catch((e) => {
                                flash.value.error(e);
                            });
                    }
                })
                .catch((e) => {
                    flash.value.error('There was an error creating your alert!');
                });
        }
    });

    loadingQuotes.value = false;
};

const updateAlert = (alertItems, alertData) => {
    if (!alertData.symbol) {
        flash.value.error('There was an error updating your alert!');
        return;
    }

    // Delete existing alerts and then create new ones
    let deleteErrors = false;

    alertItems.forEach(alertItem => {
        alertManager.value.deleteAlert(alertItem).catch((e) => {
            deleteErrors = true;
            flash.value.error('There was an error deleting the alert!');
        });
    });

    // If there were no errors in deletion, proceed to create a new alert
    if (!deleteErrors) {
        createAlert(alertData);
    }
};

const groupAlerts = (alerts) => {
    let groupedAlerts = {};
    alerts.forEach((a) => {
        let symbol = a.conditions[0].property.target.identifier;
        if (!groupedAlerts[symbol]) {
            groupedAlerts[symbol] = [];
        }
        groupedAlerts[symbol].push(a);
    });
    return groupedAlerts;
};

const retrieveAlerts = () => {
    alertManager.value.retrieveAlerts(alertsCredentials.value)
        .then((alerts) => {
            // group alerts by symbol
            priceAlertData.value.alerts = groupAlerts(alerts);

            // load the 'Quotes' data
            if (!loadingQuotes.value && !props.priceAlertAdd) {
                loadingQuotes.value = true;
                setTimeout(() => {
                    getQuoteData();
                }, 500);
            }
        })
        .catch((e) => {
            console.log(e);
        });
};

const getQuoteData = () => {
    let symbols = Object.keys(priceAlertData.value.alerts);
    if (symbols.length > 0) {
        const route = window.route('api.quotes');
        axios.post(route, { symbols: symbols.join(','), fields: 'symbol,symbolName,contractFullName,lastPrice,priceChange,percentChange,baseCode' })
            .then((response) => {
                if (response.data.errors && response.data.errors.invalidSymbols) {
                    errorSymbolQuotes.value = true;
                }
                priceAlertData.value.quotes = response.data.data;
                loadingQuotes.value = false;
            })
            .catch((error) => {
                // Handle error appropriately
                flash.value.error('Error fetching quote data');
                loadingQuotes.value = false;
            });
    } else {
        loadingQuotes.value = false;
    }
};

const initializeAlerts = () => {
    const AdapterForSocketIo = require('@barchart/alerts-client-js/lib/adapters/AdapterForSocketIo');
    const host = 'alerts-management-prod.barchart.com';
    const port = 443;
    const secure = true;

    alertManager.value = new AlertManager(host, port, secure, AdapterForSocketIo);

    const jwtGenerator = () => {
        return new Promise((resolve, reject) => {
            axios.get(window.route('my-folder.price-alerts.generate-jwt')).then(response => {
                resolve(response.data.toString());
            }).catch(error => {
                console.error('Failed to retrieve token', error);
                flash.value.error('There was an error retrieving token!');
                reject(error);
            });
        });
    };

    const jwtProvider = new JwtProvider(jwtGenerator, 60 * 1000 * 5); // Refresh token every 5 minutes

    alertManager.value.connect(jwtProvider)
        .then(() => {
            alertManager.value.retrieveAlerts(alertsCredentials.value)
                .then(() => {
                    // Initialize listeners
                    alertManager.value.subscribeAlerts(
                        alertsCredentials.value,
                        (changedAlert) => {
                            retrieveAlerts();
                        },
                        (deletedAlert) => {
                            retrieveAlerts();
                        },
                        (createdAlert) => {
                            retrieveAlerts();
                        },
                        (triggeredAlert) => {
                            if (!props.priceAlertAdd) {
                                flash.value.display(`Your alert "${triggeredAlert.name}" has triggered`);
                            }
                        }
                    );
                })
                .catch((e) => {
                    console.error('Failed to retrieve alerts', e);
                    flash.value.error('There was an error retrieving alerts!');
                });
        })
        .catch((e) => {
            console.error('Failed to connect to the Barchart Alerting Service', e);
            flash.value.error('There was an error connecting to the Barchart Alerting Service!');
        });
};

// Lifecycle hooks
onMounted(() => {
    initializeAlerts();

    if (props.priceAlertAdd) {
        priceAlertModal.value = props.priceAlertAdd;
    }
});

function cleanup() {
    alertManager.value.dispose();
    emit('close');
}

onUnmounted(() => {
    cleanup();
});
</script>

<style lang="scss">
#priceAlertSettingsModal {
    @apply text-left;

    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }
    }

    fieldset:disabled {
        span {
            @apply font-normal text-gray-500;
        }
    }
}
</style>
